import { DRUG_SORT_KEYS, type DrugSortKey } from '@/entities/drug/api/drug.api';
import { useDrugSearch } from '@/entities/drug/hooks/useDrugQueries';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { DrugFilterRail, type DrugFilterValue } from './DrugFilterRail';
import { DrugResultCard } from './DrugResultCard';

const PAGE_SIZE = 20;

const SORT_LABELS: Record<DrugSortKey, string> = {
  DRUG_NAME_ASC: '의약품 이름순',
  PHARMA_FEE_RATE_DESC: '수수료율 높은순',
};

/**
 * M-11 검색 결과 — 좌측 필터 레일(데스크톱) / 하단 Sheet(모바일) + 툴바 + 결과 카드 + 페이지.
 * 상태는 모두 URL에 있고, 이 컴포넌트는 값을 받아 그리고 바뀐 값을 올려보낸다.
 */
export const DrugSearchResults = ({
  keyword,
  filter,
  sort,
  page,
  openedDrug,
  onFilterChange,
  onSortChange,
  onPageChange,
}: {
  keyword: string;
  filter: DrugFilterValue;
  sort: DrugSortKey;
  /** 1부터 */
  page: number;
  openedDrug?: string;
  onFilterChange: (next: DrugFilterValue) => void;
  onSortChange: (sort: DrugSortKey) => void;
  onPageChange: (page: number) => void;
}) => {
  const { open } = useDrugDrawer();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState(filter);

  const { data, isPending, isError, isFetching, refetch } = useDrugSearch({
    keyword,
    isDrugTradable: filter.tradable || undefined,
    filters: filter.filters.length ? filter.filters : undefined,
    pharmaCompanyIds: filter.pharma.length ? filter.pharma : undefined,
    sortKey: sort,
    page: page - 1,
    size: PAGE_SIZE,
  });

  const filterCount =
    (filter.tradable ? 1 : 0) + filter.filters.length + filter.pharma.length;
  const totalPages = data
    ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE))
    : 1;

  const openSheet = () => {
    setDraft(filter);
    setSheetOpen(true);
  };

  return (
    <div className="flex items-start gap-5">
      {/* 데스크톱: 좌측 고정 레일 — 고르는 즉시 반영 */}
      <Card className="sticky top-6 hidden w-[292px] shrink-0 p-5 lg:block lg:p-5">
        <DrugFilterRail
          keyword={keyword}
          value={filter}
          onChange={onFilterChange}
        />
      </Card>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line py-3 pr-3.5 pl-5">
          <span className="text-[13px] leading-5 font-medium text-ink">
            검색 결과
          </span>
          <Badge tone="primary">
            {data ? data.totalCount.toLocaleString('ko-KR') : '-'}건
          </Badge>
          <span className="text-xs leading-[18px] text-ink-6">
            · 선택한 필터 {filterCount}개
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={openSheet}
            className="ml-auto lg:hidden"
          >
            <SlidersHorizontal />
            필터{filterCount > 0 && ` ${filterCount}`}
          </Button>
          <div
            role="radiogroup"
            aria-label="정렬"
            className="flex w-full gap-3 lg:ml-auto lg:w-auto"
          >
            {DRUG_SORT_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={sort === k}
                onClick={() => onSortChange(k)}
                className={cn(
                  'rounded-sm px-3.5 py-2 text-xs leading-[18px]',
                  sort === k
                    ? 'bg-ink font-medium text-white'
                    : 'bg-field text-ink-3 hover:bg-muted',
                )}
              >
                {SORT_LABELS[k]}
              </button>
            ))}
          </div>
        </div>

        {!keyword ? (
          // [나의 의약품 보기]로 들어온 경우 — 검색 API는 keyword가 필수라 입력을 기다린다
          <div className="rounded-card border border-dashed border-line bg-surface">
            <EmptyState
              className="py-7"
              message="검색어를 입력하면 거래 가능한 의약품을 보여드려요."
            />
          </div>
        ) : isPending ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-[232px] rounded-card" />
            ))}
          </div>
        ) : isError ? (
          <Card>
            <ErrorState onRetry={() => void refetch()} />
          </Card>
        ) : data.content.length === 0 ? (
          <div className="rounded-card border border-dashed border-line bg-surface">
            <EmptyState
              className="py-7"
              message="검색 결과가 없습니다. 다른 제품명 · 성분명으로 검색해보세요."
              action={
                filterCount > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      onFilterChange({
                        tradable: false,
                        filters: [],
                        pharma: [],
                      })
                    }
                  >
                    필터 초기화
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <>
            <div
              className={cn(
                'grid grid-cols-1 gap-4 transition-opacity lg:grid-cols-2',
                isFetching && 'opacity-60',
              )}
            >
              {data.content.map((d) => (
                <DrugResultCard
                  key={d.drugCode}
                  drug={d}
                  active={openedDrug === d.drugCode}
                  onOpen={() => void open(d.drugCode)}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={onPageChange}
              />
            )}
          </>
        )}
      </div>

      {/* 모바일: 하단 Sheet — [필터 적용]을 눌러야 반영 (README §8) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" overlay="popup" className="lg:hidden">
          <SheetTitle className="sr-only">필터</SheetTitle>
          <SheetDescription className="sr-only">
            검색 결과 필터를 고르고 필터 적용을 누르세요.
          </SheetDescription>
          <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
            <DrugFilterRail
              keyword={keyword}
              value={draft}
              onChange={setDraft}
            />
          </div>
          <div className="shrink-0 border-t border-line px-5 py-3">
            <Button
              size="lg"
              className="w-full"
              onClick={() => {
                onFilterChange(draft);
                setSheetOpen(false);
              }}
            >
              필터 적용
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

/** 페이지 번호 — 현재 페이지 기준 최대 5개 */
const Pagination = ({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) => {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from(
    { length: Math.min(5, totalPages) },
    (_, i) => start + i,
  );
  const navBtn =
    'flex size-9 items-center justify-center rounded-sm text-ink-4 hover:bg-muted disabled:pointer-events-none disabled:opacity-30';

  return (
    <nav
      aria-label="페이지"
      className="flex items-center justify-center gap-1 pt-2"
    >
      <button
        type="button"
        aria-label="이전 페이지"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className={navBtn}
      >
        <ChevronLeft className="size-4" />
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          aria-current={p === page ? 'page' : undefined}
          onClick={() => onChange(p)}
          className={cn(
            'size-9 rounded-sm text-[13px]',
            p === page
              ? 'bg-ink font-medium text-white'
              : 'text-ink-3 hover:bg-muted',
          )}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        aria-label="다음 페이지"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className={navBtn}
      >
        <ChevronRight className="size-4" />
      </button>
    </nav>
  );
};
